import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import  MenuItem  from "@mui/material/MenuItem";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import MainHeader from "../../Components/Headres/Main_Header";
import DatagridTable from "../../Components/Table/DataGrid_Table";
import { createColumn } from "../../Components/Table/DataGrid_Cullums";
import { fetchGetallUsers } from "../../Redux/Users_Slice";

import EditIcon from "./../../Assets/Icons/Edit.svg";

const SearchSchema = Yup.object().shape({
  firstName: Yup.string(),
  email: Yup.string(),
  isBadUser: Yup.string().oneOf(["true", "false"], "Ungültiger Status")
});

const UsersTable = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role');
  const dispatch = useDispatch();
  const { data, isLoading, error, pagination } = useSelector(
    (state) => state.Users
  );
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const [searchFilters, setSearchFilters] = useState({});

  useEffect(() => {
    dispatch(fetchGetallUsers({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      filters: searchFilters
    }));
  }, [paginationModel, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = (values) => {
    const filters = {};
    if (values.firstName) filters.firstName = values.firstName;
    if (values.email) filters.email = values.email;
    if (values.isBadUser) filters.isBadUser = values.isBadUser;
    setSearchFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const Toolbar = () => (
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ 
          firstName: "", 
          email: "", 
          isBadUser: "" 
        }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm }) => (
          <Form style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", justifyContent: "end" }}>
            <Field
              as={TextField}
              name="firstName"
              label="Vorname"
              variant="outlined"
              size="small"
              error={touched.firstName && Boolean(errors.firstName)}
              helperText={touched.firstName && errors.firstName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              name="email"
              label="E-Mail"
              variant="outlined"
              size="small"
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              select
              name="isBadUser"
              label="Bewertung"
              variant="outlined"
              size="small"
              error={touched.isBadUser && Boolean(errors.isBadUser)}
              helperText={touched.isBadUser && errors.isBadUser}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="true">Rote Karte</MenuItem>
              <MenuItem value="false">Grüne Karte</MenuItem>
            </Field>
            <Button type="submit" variant="contained" color="inherit" size="medium">
              Suchen
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Aktualisieren
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  const columns = [
    {
      ...createColumn("firstName", "Vorname", 150),
      renderCell: (params) => (
        <Button
          onClick={() => navigate(`ViewUsers/${params.row.id}`)}
          sx={{ padding: 0, textTransform: "none", color: "#CD9D41" }}
        >
          {params.value}
        </Button>
      ),
    },
    createColumn("lastName", "Nachname", 150),
    createColumn("email", "E-Mail", 200),
    createColumn("address", "Adresse", 200),
    createColumn("phoneNumber", "Telefonnummer", 150),
    createColumn("adminNote", "Admin-Notizen", 250),
    createColumn("numberOfReservations", "Anzahl der Buchungen", 150),
    createColumn("isBadUser", "Bewertung", 120, "boolean", (params) => (
      <Box
        sx={{
          bgcolor: params.value ? "#C03C3C38" : "#36D06A38",
          borderRadius: "14px",
          minWidth: 110,
          display: "inline-block",
          py: "5px",
          border: `3px solid ${params.value ? "#C03C3C38" : "#36D06A38"}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            color: params.value ? "#C03C3C" : "#36D06A",
            textAlign: "center",
          }}
        >
          {params.value ? "Rote Karte" : "Grüne Karte"}
        </Typography>
      </Box>
    )),
    createColumn("isVerified", "Verifiziert", 120, "boolean"),
    createColumn("isActive", "Aktiv", 120),
    ...(role === 'manager' ? [
    createColumn("aktion", "Aktion", 150, "actions", (params) => (
      <Box>
        <IconButton
          color="error"
          size="small"
          onClick={() => navigate(`UpdataUser/${params.row.id}`)}
        >
          <img style={{ height: "30px" }} src={EditIcon} alt="Edit" />
        </IconButton>
      </Box>
        ))
      ] : [])
    ];

  return (
    <Box>
      <MainHeader 
        title="Neuen Kunden" 
        onNavigate={() => navigate("CreatUser")} 
      />
      <Box sx={{ height: 400, width: "100%" }}>
      <DatagridTable
          columns={columns}
          rows={data || []}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
          toolbarComponent={<Toolbar />}
        />
      </Box>
    </Box>
  );
};

export default UsersTable;